import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { DatePicker } from "@/components/ui/date-picker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { CalendarCheck, Plus, Loader2, Check, X, Trash2, User, Mail, Phone, Users as UsersIcon, Link2, Pencil, AlertTriangle, MessageCircle, Send, Search, Copy, ExternalLink, Key, Sparkles, LogOut, ChevronDown, ChevronRight, Moon } from "lucide-react";
import { formatDate, formatDateTime } from "@/lib/date";
import { SesButton } from "@/components/SesButton";
import { CashToggle } from "@/components/CashToggle";
import { GuestMessagesToggle } from "@/components/GuestMessagesToggle";
import { toast } from "sonner";

import { HistoryJumpBar } from "@/components/HistoryJumpBar";
import { useNotifications } from "@/context/NotificationsContext";
import { tx, lz } from "@/i18n";
const empty = { house_id: "", guest_name: "", guest_email: "", guest_phone: "", arrival: "", departure: "", num_guests: "", party_composition: "", register_ses: false, notes: "", stay_type: "totaalpakket", cash: false };
const phoneOk = (v) => /^\+[1-9]\d{7,14}$/.test((v || "").replace(/[\s\-()]/g, ""));
const RES_ICONS = lz([
  { cat: "checkin", Icon: Key, label: "Check-in" },
  { cat: "checkout", Icon: LogOut, label: "Check-out" },
  { cat: "cleaning", Icon: Sparkles, label: "Schoonmaak" },
]);
const taskComplete = (t) => !!t && (t.status === "done" || (Array.isArray(t.subtasks) && t.subtasks.length > 0 && t.subtasks.every((s) => s.done)));

const ConflictBanner = ({ conflicts }) => {
  if (!conflicts || conflicts.length === 0) return null;
  return (
    <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/40 text-destructive text-sm" data-testid="conflict-warning">
      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
      <div>
        <p className="font-semibold">{tx("De woning is in deze periode al bezet — opslaan geblokkeerd")}</p>
        <ul className="mt-1 list-disc list-inside">
          {conflicts.map((c) => (
            <li key={c.id}>{c.guest_name} · {formatDate(c.arrival)} → {formatDate(c.departure)}</li>
          ))}
        </ul>
        <p className="mt-1 text-xs">{tx("Kies een andere aankomst- of vertrekdatum die niet overlapt.")}</p>
      </div>
    </div>
  );
};

const STATUS = lz({
  pending: { label: "In afwachting", cls: "bg-amber-100 text-amber-700" },
  confirmed: { label: "Bevestigd", cls: "bg-secondary/15 text-secondary" },
  rejected: { label: "Afgewezen", cls: "bg-red-100 text-red-700" },
});

export default function Reservations() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [expandedHistory, setExpandedHistory] = useState(() => new Set());
  const [houses, setHouses] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(empty);
  const [editSaving, setEditSaving] = useState(false);
  const [sendingWa, setSendingWa] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [msgFor, setMsgFor] = useState(null);
  const [messages, setMessages] = useState(null);
  const [search, setSearch] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [tasks, setTasks] = useState([]);
  const isOwner = user.role === "client";
  const isAdmin = user.role === "admin";
  const isTeam = ["admin"].includes(user.role);
  const canCreate = isOwner || isTeam;

  const { refresh: refreshNotif } = useNotifications();
  const load = () => api.get("/reservations").then(({ data }) => { setItems(data); refreshNotif(); }).catch(() => setItems([]));
  const loadTasks = () => api.get("/tasks").then(({ data }) => setTasks(data)).catch(() => setTasks([]));
  useEffect(() => {
    load();
    if (user.role !== "guest") loadTasks();
    if (canCreate) api.get("/houses").then(({ data }) => setHouses(data)).catch(() => {});
  }, []);
  const taskFor = (resId, cat) => tasks.find((t) => t.reservation_id === resId && t.category === cat);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const setEdit = (k) => (e) => setEditForm({ ...editForm, [k]: e?.target ? e.target.value : e });
  const isOwnStay = form.stay_type === "eigen_verblijf";
  const isOwnStayEdit = editForm.stay_type === "eigen_verblijf";

  const findConflicts = (houseId, arrival, departure, excludeId) => {
    if (!items || !houseId || !arrival || !departure) return [];
    return items.filter((r) =>
      r.id !== excludeId &&
      r.house_id === houseId &&
      r.status !== "rejected" &&
      (r.arrival || "").slice(0, 10) < departure &&
      arrival < (r.departure || "").slice(0, 10)
    );
  };
  const createConflicts = findConflicts(form.house_id, form.arrival, form.departure, null);
  const editConflicts = editing ? findConflicts(editForm.house_id, editForm.arrival, editForm.departure, editing.id) : [];
  const datesInvalid = (a, d) => !!a && !!d && d.slice(0, 10) <= a.slice(0, 10);
  const createDateInvalid = datesInvalid(form.arrival, form.departure);
  const editDateInvalid = datesInvalid(editForm.arrival, editForm.departure);

  // Bezette nachten per woning. Voor AANKOMST: nachten [aankomst, vertrek-1] zijn geblokkeerd.
  // Voor VERTREK: een vertrekdatum is ongeldig als de nacht ervóór bezet is -> [aankomst+1, vertrek].
  // Zo blijft same-day turnover mogelijk: nieuwe check-in op bestaande check-out dag, én
  // nieuwe check-out op bestaande check-in dag.
  const occupiedRanges = (houseId, excludeId, forDeparture = false) => {
    if (!items || !houseId) return [];
    return items
      .filter((r) => r.id !== excludeId && r.house_id === houseId && r.status !== "rejected" && r.arrival && r.departure)
      .map((r) => {
        const arr = new Date(`${(r.arrival).slice(0, 10)}T00:00:00`);
        const dep = new Date(`${(r.departure).slice(0, 10)}T00:00:00`);
        const from = forDeparture ? new Date(arr.getTime() + 86400000) : arr;
        const to = forDeparture ? dep : new Date(dep.getTime() - 86400000);
        return { from, to: to >= from ? to : from };
      });
  };
  const createRanges = occupiedRanges(form.house_id, null, false);
  const createDepRanges = occupiedRanges(form.house_id, null, true);
  const editRanges = editing ? occupiedRanges(editForm.house_id, editing.id, false) : [];
  const editDepRanges = editing ? occupiedRanges(editForm.house_id, editing.id, true) : [];

  const openEdit = (r) => {
    setEditing(r);
    setEditForm({
      house_id: r.house_id || "",
      guest_name: r.guest_name || "",
      guest_email: r.guest_email || "",
      guest_phone: r.guest_phone || "",
      arrival: (r.arrival || "").slice(0, 10),
      departure: (r.departure || "").slice(0, 10),
      num_guests: r.num_guests || 1,
      party_composition: r.party_composition || "",
      register_ses: !!r.register_ses,
      notes: r.notes || "",
      stay_type: r.stay_type || "totaalpakket",
      cash: !!r.cash,
    });
  };

  const saveEdit = async () => {
    if (editDateInvalid) { toast.error(tx("De vertrekdatum moet na de aankomstdatum liggen.")); return; }
    if (editConflicts.length > 0) { toast.error(tx("De woning is in deze periode al bezet. Kies een andere periode.")); return; }
    setEditSaving(true);
    try {
      const payload = { ...editForm, num_guests: Number(editForm.num_guests) || 1 };
      if (isOwnStayEdit) { payload.guest_name = null; payload.guest_email = null; payload.guest_phone = null; }
      await api.put(`/reservations/${editing.id}`, payload);
      toast.success(tx("Boeking bijgewerkt — agenda en taken zijn gesynchroniseerd"));
      setEditing(null); load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setEditSaving(false); }
  };

  const save = async () => {
    if (createDateInvalid) { toast.error(tx("De vertrekdatum moet na de aankomstdatum liggen.")); return; }
    if (createConflicts.length > 0) { toast.error(tx("De woning is in deze periode al bezet. Kies een andere periode.")); return; }
    setSaving(true);
    try {
      const payload = { ...form, num_guests: Number(form.num_guests) || 1 };
      if (isOwnStay) { payload.guest_name = null; payload.guest_email = null; payload.guest_phone = null; }
      await api.post("/reservations", payload);
      toast.success(tx("Boeking ingediend! Costa Key bevestigt deze."));
      setOpen(false); setForm(empty); load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const act = async (id, action) => {
    try {
      await api.patch(`/reservations/${id}/${action}`);
      toast.success(action === "confirm" ? tx("Bevestigd en in agenda gezet") : tx("Afgewezen"));
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const del = async (id) => { if (!window.confirm(tx("Boeking verwijderen?"))) return; try { await api.delete(`/reservations/${id}`); load(); } catch (err) { toast.error(apiError(err)); } };
  const cycleSes = async (id) => { try { await api.patch(`/reservations/${id}/ses-status`); load(); } catch (err) { toast.error(apiError(err)); } };
  const toggleCash = async (id, current) => { try { await api.patch(`/reservations/${id}/cash`, null, { params: { cash: !current } }); load(); } catch (err) { toast.error(apiError(err)); } };
  const magicUrl = (token) => `${window.location.origin}/portal/guest/${token}`;
  const copyLink = async (token, id) => {
    const url = magicUrl(token);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        throw new Error("no clipboard");
      }
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); } catch { /* ignore */ }
      document.body.removeChild(ta);
    }
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1800);
    toast.success(tx("Magic link gekopieerd — deel 'm met je gast (bijv. via WhatsApp of e-mail)"));
  };
  const houseName = (id) => houses.find((h) => h.id === id)?.name;

  const q = search.trim().toLowerCase();
  const filtered = (items || []).filter((r) => {
    if (!q) return true;
    const hay = [r.guest_name, r.house_name || houseName(r.house_id), r.notes, r.party_composition]
      .filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q);
  });
  // Eigenaar: naar Historie zodra de vertrekdatum voorbij is. Anderen: zodra de schoonmaak is afgerond.
  const cleaningDone = (r) => taskComplete(taskFor(r.id, "cleaning"));
  const todayStr = new Date().toLocaleDateString("sv-SE");
  const isHist = (r) => (isOwner ? !!r.departure && r.departure.slice(0, 10) < todayStr : cleaningDone(r));
  const pendingFirst = (r) => (isAdmin && r.status === "pending" ? 0 : 1);
  const active = filtered.filter((r) => !isHist(r))
    .sort((a, c) => pendingFirst(a) - pendingFirst(c) || new Date(a.arrival || 0) - new Date(c.arrival || 0));
  const history = filtered.filter((r) => isHist(r))
    .sort((a, c) => new Date(c.departure || 0) - new Date(a.departure || 0));

  const sendPrearrival = async (id) => {
    setSendingWa(id);
    try {
      await api.post(`/reservations/${id}/send-prearrival`);
      toast.success(tx("WhatsApp verstuurd — gevraagd naar aankomst-/vertrektijd"));
    } catch (err) { toast.error(apiError(err)); }
    finally { setSendingWa(null); }
  };
  const sendPrearrivalEmail = async (id) => {
    setSendingEmail(id);
    try {
      await api.post(`/reservations/${id}/send-prearrival-email`);
      toast.success(tx("Uitvraag tijden verzonden per e-mail"));
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSendingEmail(null); }
  };

  const openMessages = async (r) => {
    setMsgFor(r); setMessages(null);
    try { const { data } = await api.get(`/messages?reservation_id=${r.id}`); setMessages(data); }
    catch { setMessages([]); }
  };

  return (
    <div className="animate-fade-up">
      <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight">{tx("Boekingen")}</h1>
          <p className="text-muted-foreground mt-1">{isOwner ? tx("Dien een boeking in voor je woning.") : tx("Bevestig boekingen van eigenaren.")}</p>
        </div>
        {user?.role === "admin" && <GuestMessagesToggle />}
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90 gap-2" data-testid="add-reservation-btn"><Plus className="h-4 w-4" /> {tx("Nieuwe boeking")}</Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display">{tx("Nieuwe boeking")}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>{tx("Woning *")}</Label>
                  <Select value={form.house_id} onValueChange={set("house_id")}>
                    <SelectTrigger className="mt-1" data-testid="res-house-select"><SelectValue placeholder={tx("Kies woning")} /></SelectTrigger>
                    <SelectContent>{houses.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{tx("Type verblijf *")}</Label>
                  <Select value={form.stay_type} onValueChange={set("stay_type")}>
                    <SelectTrigger className="mt-1" data-testid="res-stay-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="totaalpakket">{tx("Verhuur")}</SelectItem>
                      <SelectItem value="eigen_verblijf">{tx("Eigen verblijf (alleen schoonmaak + linnenservice)")}</SelectItem>
                    </SelectContent>
                  </Select>
                  {isOwnStay && <p className="text-xs text-muted-foreground mt-1" data-testid="res-eigen-hint">{tx("Eigen verblijf: geen gastgegevens, geen pre-arrival mail of magic link. Alleen een schoonmaak wordt ingepland.")}</p>}
                </div>
                {!isOwnStay && (
                  <>
                    <div><Label>{tx("Volledige naam hoofdboeker *")}</Label><Input value={form.guest_name} onChange={set("guest_name")} className="mt-1" data-testid="res-name-input" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>{tx("E-mailadres *")}</Label><Input type="email" value={form.guest_email} onChange={set("guest_email")} className="mt-1" data-testid="res-email-input" /></div>
                      <div><Label>{tx("Mobiel (incl. landcode) *")}</Label><Input value={form.guest_phone} onChange={set("guest_phone")} className="mt-1" placeholder="+31612345678" data-testid="res-phone-input" />{form.guest_phone && !phoneOk(form.guest_phone) && <p className="text-xs text-destructive mt-1" data-testid="res-phone-error">{tx("Gebruik internationaal formaat, bijv. +31612345678")}</p>}</div>
                    </div>
                  </>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>{tx("Aankomst *")}</Label><DatePicker value={form.arrival} onChange={set("arrival")} className="mt-1" testId="res-arrival-input" disabledDates={createRanges} /></div>
                  <div><Label>{tx("Vertrek *")}</Label><DatePicker value={form.departure} onChange={set("departure")} className="mt-1" testId="res-departure-input" disabledDates={createDepRanges} /></div>
                </div>
                {createDateInvalid && <p className="text-xs text-destructive" data-testid="res-date-error">{tx("De vertrekdatum moet na de aankomstdatum liggen.")}</p>}
                <div><Label>{tx("Aantal")} {isOwnStay ? "personen" : "gasten"} *</Label><Input type="number" min="1" value={form.num_guests} onChange={set("num_guests")} className="mt-1" data-testid="res-guests-input" /></div>
                {!isOwnStay && (
                  <>
                    <div><Label>{tx("Samenstelling / leeftijden reisgezelschap")}</Label><Textarea value={form.party_composition} onChange={set("party_composition")} className="mt-1" /></div>
                    <div className="flex items-center justify-between p-3 rounded-lg border border-border">
                      <Label className="cursor-pointer">{tx("Gasten aanmelden bij SES.Hospedajes?")}</Label>
                      <Switch checked={form.register_ses} onCheckedChange={set("register_ses")} data-testid="res-ses-switch" />
                    </div>
                    <div><Label>{tx("Toelichting / bijzonderheden")}</Label><Textarea value={form.notes} onChange={set("notes")} className="mt-1" /></div>
                  </>
                )}
                <ConflictBanner conflicts={createConflicts} />
                <div className="flex justify-end pt-1">
                  <CashToggle value={form.cash} canEdit onToggle={() => set("cash")(!form.cash)} testId="res-cash-toggle" />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={save} disabled={saving || createConflicts.length > 0 || createDateInvalid || !form.house_id || !form.num_guests || Number(form.num_guests) < 1 || !form.arrival || !form.departure || (!isOwnStay && (!form.guest_name || !form.guest_email || !phoneOk(form.guest_phone)))} className="bg-primary hover:bg-primary/90" data-testid="save-reservation-btn">
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {tx("Indienen")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {!items ? (
        <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center text-muted-foreground">
          <CalendarCheck className="h-10 w-10 mx-auto mb-3 opacity-40" /> {tx("Nog geen boekingen.")}
        </div>
      ) : (
        <>
          <div className="relative mb-4 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={tx("Zoek op gast, woning of opmerking…")}
              className="pl-9 pr-9"
              data-testid="reservation-search-input"
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="reservation-search-clear">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <HistoryJumpBar count={history.length} upcomingId="reservations-top-anchor" historyId="reservation-history-anchor" onOpenHistory={() => setHistoryOpen(true)} testPrefix="reservations" />
          {filtered.length === 0 ? (
            <div className="bg-card border border-border rounded-xl p-12 text-center text-muted-foreground" data-testid="reservation-no-results">
              <Search className="h-10 w-10 mx-auto mb-3 opacity-40" /> {tx("Geen boekingen gevonden voor \"")}{search}".
            </div>
          ) : (
            <div id="reservations-top-anchor" className="space-y-3">
              {[
                ...active.map((r) => ({ r })),
                ...(history.length ? [{ divider: true }] : []),
                ...history.map((r) => ({ r, hist: true })),
              ].map((item) => {
            if (item.divider) {
              return (
                <button type="button" key="__history_divider__" id="reservation-history-anchor" onClick={() => setHistoryOpen((o) => !o)} className="flex items-center gap-3 w-full pt-4" data-testid="reservation-history-header">
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                    {historyOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />} {tx("Historie (")}{history.length})
                  </span>
                  <div className="h-px flex-1 bg-border" />
                </button>
              );
            }
            const r = item.r;
            if (item.hist && !historyOpen) return null;
            if (item.hist && !expandedHistory.has(r.id)) {
              return (
                <button type="button" key={r.id} onClick={() => setExpandedHistory((s) => new Set(s).add(r.id))} className="w-full flex items-center justify-between gap-3 px-4 py-2 rounded-lg border border-border bg-card/60 text-sm hover:bg-accent/40 transition-colors text-left" data-testid={`reservation-history-row-${r.id}`}>
                  <span className="font-medium truncate">{r.house_name || houseName(r.house_id)}</span>
                  <span className="text-muted-foreground flex items-center gap-1.5 shrink-0"><CalendarCheck className="h-3.5 w-3.5" /> {formatDate(r.arrival)} → {formatDate(r.departure)}</span>
                </button>
              );
            }
            const st = STATUS[r.status] || STATUS.pending;
            return (
              <div key={r.id} className="bg-card rounded-xl border border-border p-5 shadow-sm" data-testid={`reservation-${r.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${st.cls}`} data-testid={`res-status-${r.id}`}>{st.label}</span>
                      <h3 className="font-display font-semibold">{r.house_name || houseName(r.house_id)}</h3>
                      {user.role !== "guest" && r.status === "confirmed" && (
                        <span className="flex items-center gap-1.5 ml-0.5" data-testid={`res-status-icons-${r.id}`}>
                          {RES_ICONS.map(({ cat, Icon, label }) => {
                            const d = taskComplete(taskFor(r.id, cat));
                            return <span key={cat} title={`${label}${d ? tx(" — checklist voltooid") : tx(" — checklist open")}`} className={d ? "text-emerald-600" : "text-muted-foreground/40"} data-testid={`res-icon-${cat}-${r.id}`}><Icon className="h-4 w-4" /></span>;
                          })}
                        </span>
                      )}
                      {r.register_ses && <SesButton status={r.ses_status} canEdit={user.role === "admin"} onCycle={() => cycleSes(r.id)} testId={`res-ses-${r.id}`} />}
                      {r.night_moves?.length > 0 && (
                        <span title={r.night_moves.map((n) => `${n.type === "checkin" ? tx("Incheck") : tx("Uitcheck")} ${n.time}`).join(" · ") + ` — ${tx("Late in- of uitcheck (nachttoeslag)")}`} className="text-indigo-600" data-testid={`res-night-${r.id}`}><Moon className="h-4 w-4" /></span>
                      )}
                      {r.stay_type === "eigen_verblijf" && <Badge variant="outline" className="text-xs border-secondary text-secondary" data-testid={`res-eigen-badge-${r.id}`}>{tx("Eigen verblijf")}</Badge>}
                      <CashToggle value={r.cash} canEdit={user.role === "admin"} onToggle={() => toggleCash(r.id, r.cash)} testId={`res-cash-${r.id}`} />
                    </div>
                    <div className="text-sm text-muted-foreground mt-2 flex flex-wrap gap-x-4 gap-y-1">
                      <span className="flex items-center gap-1.5"><CalendarCheck className="h-3.5 w-3.5" /> {formatDate(r.arrival)} → {formatDate(r.departure)}</span>
                      <span className="flex items-center gap-1.5"><User className="h-3.5 w-3.5" /> {r.guest_name}</span>
                      <span className="flex items-center gap-1.5"><UsersIcon className="h-3.5 w-3.5" /> {r.num_guests} gasten</span>
                      {r.guest_email && <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> {r.guest_email}</span>}
                      {r.guest_phone && <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {r.guest_phone}</span>}
                    </div>
                    {r.party_composition && <p className="text-sm text-muted-foreground mt-2">{r.party_composition}</p>}
                    {r.notes && <p className="text-sm text-muted-foreground mt-1 italic">"{r.notes}"</p>}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {item.hist && (
                      <Button size="sm" variant="ghost" onClick={() => setExpandedHistory((s) => { const n = new Set(s); n.delete(r.id); return n; })} className="gap-1.5 text-muted-foreground" data-testid={`reservation-history-collapse-${r.id}`}><ChevronDown className="h-4 w-4" /> {tx("Inklappen")}</Button>
                    )}
                    {isTeam && r.status === "confirmed" && r.guest_phone && (
                      <Button size="sm" variant="outline" onClick={() => sendPrearrival(r.id)} disabled={sendingWa === r.id} className="gap-1.5 border-green-500 text-green-600 hover:bg-green-50" data-testid={`wa-prearrival-${r.id}`}>
                        {sendingWa === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircle className="h-4 w-4" />} {tx("Vraag tijden (WhatsApp)")}
                      </Button>
                    )}
                    {isTeam && (
                      <Button size="sm" variant="ghost" onClick={() => openMessages(r)} className="gap-1.5 text-muted-foreground" data-testid={`msg-log-${r.id}`}>
                        <Send className="h-4 w-4" /> {tx("Berichten")}
                      </Button>
                    )}
                    {isTeam && r.status === "pending" && (
                      <>
                        <Button size="sm" onClick={() => act(r.id, "confirm")} className="bg-secondary hover:bg-secondary/90 gap-1.5" data-testid={`confirm-res-${r.id}`}><Check className="h-4 w-4" /> {tx("Bevestig")}</Button>
                        <Button size="sm" variant="outline" onClick={() => act(r.id, "reject")} className="gap-1.5 text-destructive border-destructive/30" data-testid={`reject-res-${r.id}`}><X className="h-4 w-4" /> {tx("Afwijzen")}</Button>
                      </>
                    )}
                    {user.role === "admin" && r.status !== "rejected" && (
                      <Button size="sm" variant="outline" onClick={() => openEdit(r)} className="gap-1.5" data-testid={`edit-res-${r.id}`}><Pencil className="h-4 w-4" /> {tx("Bewerken")}</Button>
                    )}
                    {user.role === "admin" && <Button size="icon" variant="ghost" onClick={() => del(r.id)} className="text-destructive" data-testid={`delete-res-${r.id}`}><Trash2 className="h-4 w-4" /></Button>}
                  </div>
                </div>
                {user.role === "admin" && r.status !== "rejected" && r.stay_type !== "eigen_verblijf" && (
                  <div className="mt-4 pt-4 border-t border-border flex items-center justify-between gap-3 flex-wrap" data-testid={`prearrival-email-block-${r.id}`}>
                    <span className="text-xs flex items-center gap-1.5" data-testid={`prearrival-status-${r.id}`}>
                      <Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      {r.prearrival_email_sent ? (
                        <span className="text-green-600 font-medium">{tx("Uitvraag tijden verzonden")}{r.prearrival_email_sent_at ? tx(" op {0}", { 0: formatDateTime(r.prearrival_email_sent_at) }) : ""}</span>
                      ) : r.prearrival_email_at ? (
                        <span className="text-muted-foreground">{tx("Uitvraag tijden gepland op")} {formatDateTime(r.prearrival_email_at)}</span>
                      ) : (
                        <span className="text-muted-foreground">{tx("Uitvraag tijden nog niet gepland")}</span>
                      )}
                    </span>
                    {!r.prearrival_email_sent && (
                      <Button size="sm" variant="outline" onClick={() => sendPrearrivalEmail(r.id)} disabled={sendingEmail === r.id || !r.guest_email} className="gap-1.5" data-testid={`send-prearrival-email-${r.id}`}>
                        {sendingEmail === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {tx("Nu versturen")}
                      </Button>
                    )}
                  </div>
                )}
                {r.status !== "rejected" && r.welcome_token && r.stay_type !== "eigen_verblijf" && user.role === "admin" && (
                  <div className="mt-4 pt-4 border-t border-border" data-testid={`magic-link-block-${r.id}`}>
                    <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
                      <span className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5"><Link2 className="h-3.5 w-3.5" /> {tx("Gastenportaal · magic link")}</span>
                      {r.welcome_expiry && (
                        <span className={`text-xs ${r.welcome_expired ? "text-destructive font-medium" : "text-muted-foreground"}`} data-testid={`magic-expiry-${r.id}`}>
                          {r.welcome_expired ? tx("Verlopen op ") : tx("Geldig t/m ")}{formatDate(r.welcome_expiry)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Input readOnly value={magicUrl(r.welcome_token)} onFocus={(e) => e.target.select()} className={`text-sm bg-muted/40 ${r.welcome_expired ? "line-through text-muted-foreground" : ""}`} data-testid={`magic-link-input-${r.id}`} />
                      <Button size="sm" variant="outline" onClick={() => copyLink(r.welcome_token, r.id)} className="gap-1.5 shrink-0 border-secondary text-secondary hover:bg-secondary/10" data-testid={`copy-welcome-${r.id}`}>
                        {copiedId === r.id ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copiedId === r.id ? tx("Gekopieerd") : tx("Kopieer link")}
                      </Button>
                      {!r.welcome_expired && (
                        <a href={magicUrl(r.welcome_token)} target="_blank" rel="noreferrer" className="h-9 w-9 grid place-items-center rounded-md border border-border text-muted-foreground hover:bg-accent shrink-0" data-testid={`open-welcome-${r.id}`} title={tx("Open gastpagina")}>
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1.5">{tx("Directe toegang tot welkomstboek én chat, zonder inloggen. Verloopt automatisch 1 dag na de uitcheckdatum.")}</p>
                  </div>
                )}
              </div>
            );
          })}
            </div>
          )}
        </>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="edit-reservation-dialog">
          <DialogHeader><DialogTitle className="font-display">{tx("Boeking bewerken")}</DialogTitle><DialogDescription>{tx("Pas datums en gastgegevens aan; agenda en taken worden automatisch bijgewerkt.")}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">{tx("Woning:")} <span className="font-medium text-foreground">{editing && (editing.house_name || houseName(editing.house_id))}</span> {tx("(kan niet gewijzigd worden)")}</div>
            <div>
              <Label>{tx("Type verblijf *")}</Label>
              <Select value={editForm.stay_type} onValueChange={setEdit("stay_type")}>
                <SelectTrigger className="mt-1" data-testid="edit-res-stay-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="totaalpakket">{tx("Verhuur")}</SelectItem>
                  <SelectItem value="eigen_verblijf">{tx("Eigen verblijf (alleen schoonmaak + linnenservice)")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {!isOwnStayEdit && (
              <>
                <div><Label>{tx("Volledige naam hoofdboeker *")}</Label><Input value={editForm.guest_name} onChange={setEdit("guest_name")} className="mt-1" data-testid="edit-res-name-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>{tx("E-mailadres *")}</Label><Input type="email" value={editForm.guest_email} onChange={setEdit("guest_email")} className="mt-1" data-testid="edit-res-email-input" /></div>
                  <div><Label>{tx("Mobiel (incl. landcode) *")}</Label><Input value={editForm.guest_phone} onChange={setEdit("guest_phone")} className="mt-1" placeholder="+31612345678" data-testid="edit-res-phone-input" />{editForm.guest_phone && !phoneOk(editForm.guest_phone) && <p className="text-xs text-destructive mt-1" data-testid="edit-res-phone-error">{tx("Gebruik internationaal formaat, bijv. +31612345678")}</p>}</div>
                </div>
              </>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{tx("Aankomst *")}</Label><DatePicker value={editForm.arrival} onChange={setEdit("arrival")} className="mt-1" testId="edit-res-arrival-input" disabledDates={editRanges} /></div>
              <div><Label>{tx("Vertrek *")}</Label><DatePicker value={editForm.departure} onChange={setEdit("departure")} className="mt-1" testId="edit-res-departure-input" disabledDates={editDepRanges} /></div>
            </div>
            {editDateInvalid && <p className="text-xs text-destructive" data-testid="edit-res-date-error">{tx("De vertrekdatum moet na de aankomstdatum liggen.")}</p>}
            <div><Label>{tx("Aantal")} {isOwnStayEdit ? "personen" : "gasten"} *</Label><Input type="number" min="1" value={editForm.num_guests} onChange={setEdit("num_guests")} className="mt-1" data-testid="edit-res-guests-input" /></div>
            {!isOwnStayEdit && (
              <>
                <div><Label>{tx("Samenstelling / leeftijden reisgezelschap")}</Label><Textarea value={editForm.party_composition} onChange={setEdit("party_composition")} className="mt-1" /></div>
                <div className="flex items-center justify-between p-3 rounded-lg border border-border">
                  <Label className="cursor-pointer">{tx("Gasten aanmelden bij SES.Hospedajes?")}</Label>
                  <Switch checked={editForm.register_ses} onCheckedChange={setEdit("register_ses")} data-testid="edit-res-ses-switch" />
                </div>
                <div><Label>{tx("Toelichting / bijzonderheden")}</Label><Textarea value={editForm.notes} onChange={setEdit("notes")} className="mt-1" /></div>
              </>
            )}
            <ConflictBanner conflicts={editConflicts} />
            <div className="flex justify-end pt-1">
              <CashToggle value={editForm.cash} canEdit onToggle={() => setEdit("cash")(!editForm.cash)} testId="edit-res-cash-toggle" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveEdit} disabled={editSaving || editConflicts.length > 0 || editDateInvalid || !editForm.num_guests || Number(editForm.num_guests) < 1 || !editForm.arrival || !editForm.departure || (!isOwnStayEdit && (!editForm.guest_name || !editForm.guest_email || !phoneOk(editForm.guest_phone)))} className="bg-primary hover:bg-primary/90" data-testid="save-edit-reservation-btn">
              {editSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {tx("Opslaan")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!msgFor} onOpenChange={(o) => !o && setMsgFor(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="messages-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{tx("Verzonden berichten")}</DialogTitle>
            <DialogDescription>{msgFor && `${msgFor.guest_name} · ${msgFor.house_name || houseName(msgFor.house_id)}`}</DialogDescription>
          </DialogHeader>
          {messages === null ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : messages.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">{tx("Nog geen berichten verstuurd voor deze boeking.")}</p>
          ) : (
            <div className="space-y-3">
              {messages.map((m) => (
                <div key={m.id} className="rounded-lg border border-border p-3" data-testid={`message-${m.id}`}>
                  <div className="flex items-center justify-between gap-2 mb-1.5 text-xs">
                    <span className="inline-flex items-center gap-1.5 font-medium">
                      <MessageCircle className="h-3.5 w-3.5 text-green-600" /> {tx("WhatsApp ·")} {m.to}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full font-semibold ${m.ok ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                      {m.ok ? (m.status || "verzonden") : (m.error ? "mislukt" : m.status)}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground whitespace-pre-line">{m.body}</p>
                  <p className="text-[11px] text-muted-foreground mt-2">{formatDateTime(m.created_at)}</p>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
